// The landing page's specimen, and the descent that reaches it.
//
// WHY THERE IS NO IMAGE FILE HERE. The old landing page was `init-det.jpg`, baked
// in 2020 by a colour algorithm this code no longer contains — teal and blue, from
// before smoothed escape counts, histogram equalisation and the OKLab ramp. So the
// first thing a visitor saw was an image the site could not produce, on a product
// whose whole proposition is "this exact image is yours". The hero is now computed
// by the same renderer the visitor's own zooms use, from the four numbers below.
// Change them and the landing image changes; there is nothing to re-bake.
//
// WHY A CENTRE AND A SPAN, NOT FOUR BOUNDS. The hero is rendered live at the
// visitor's own viewport aspect and fitted, not cropped — so the coordinates shown
// on the plate are exactly the coordinates on screen, on a phone and on a desktop
// alike. Fixed bounds cropped to fill the screen would put a picture on the page
// that its own caption did not describe. Same fit-don't-crop rule as fitWholeSet
// and the gallery's object-fit: contain.

import { SET_CENTRE, fitWholeSet } from './renderSize';

// The region the site opens on. This is the specimen recorded by hand at the end of
// `fractal-color-algorithm-brief.md` ("a nice id: 6194c4") — the one region in this
// repo's history somebody looked at and decided to write down.
//
// Declared as the extent that must always remain visible: whichever axis the
// viewport makes binding is the one that grows.
export const HERO_CENTRE = { r: -0.746304955, i: 0.13549081 };
export const HERO_REAL_SPAN = 1.201e-5;
export const HERO_IMAGINARY_SPAN = 8.88e-6;

const boundsFrom = (centre, realSpan, imaginarySpan) => ({
    realMin: centre.r - realSpan / 2,
    realMax: centre.r + realSpan / 2,
    imaginaryMin: centre.i - imaginarySpan / 2,
    imaginaryMax: centre.i + imaginarySpan / 2
});

// The hero's bounds for a given viewport aspect. The declared region is always
// fully inside the result, so nothing of the specimen is ever cut off.
export const heroBounds = (aspect) => {
    const safeAspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
    const declared = HERO_REAL_SPAN / HERO_IMAGINARY_SPAN;

    let realSpan = HERO_REAL_SPAN;
    let imaginarySpan = HERO_IMAGINARY_SPAN;
    if (safeAspect > declared) {
        realSpan = imaginarySpan * safeAspect;
    } else {
        imaginarySpan = realSpan / safeAspect;
    }
    return boundsFrom(HERO_CENTRE, realSpan, imaginarySpan);
};

// ---- The descent ---------------------------------------------------------------
// The landing page's signature: the path from the whole set down to the hero
// specimen, as a row of frames. Five rungs — four rendered, plus the screen the
// visitor is already looking at, which is drawn as an empty frame because its
// contents are the page itself.
//
// Derived from the hero, never listed, so it can never fall out of step with the
// image behind it. It is a plausible descent rather than a preserved one: each rung
// is a real, valid region at real coordinates, spaced geometrically in scale.
//
// FRAMING, AND WHY IT IS NOT AN INTERPOLATION. The first version walked the centre
// from the set to the specimen in proportion to how far the frame had tightened,
// which kept the target a tidy 10% off-centre in every rung and looked like a
// hand-flown descent. It also put rung 2's centre at |1-sqrt(1-4c)| = 0.9993 —
// a hair INSIDE the main cardioid — so that frame came out as interior near-black
// with a sliver of exterior, and read as a broken image in the middle of the row.
//
// Interior is most of the plane near the set, so any path that wanders off the
// target is liable to land in it. The rule is therefore: rung 0 frames the whole
// set, so it is centred on the set; every deeper rung is centred on the specimen,
// because it is going somewhere specific. That also makes the deep rungs concentric,
// which is what makes them read as one descent rather than four separate places.
//
// Rung 0 is the whole-set framing, which means the ladder begins on precisely the
// view "Start zooming" hands you. The row is therefore a true statement of the
// product's loop: this is where you start, this is where you can get to.
export const DESCENT_FRAMES = 4;

// The frames are fixed-aspect boxes in the layout, so the framings are computed at
// that aspect rather than the viewport's. 16:9 matches the CSS box.
export const LADDER_ASPECT = 16 / 9;

export const heroDescent = () => {
    const top = fitWholeSet(LADDER_ASPECT);
    const topRealSpan = top.realMax - top.realMin;

    // Geometric in scale across all five rungs; we render the first four.
    const ratio = Math.pow(HERO_REAL_SPAN / topRealSpan, 1 / DESCENT_FRAMES);

    return Array.from({ length: DESCENT_FRAMES }, (_, index) => {
        const realSpan = topRealSpan * Math.pow(ratio, index);
        const imaginarySpan = realSpan / LADDER_ASPECT;

        // Rung 0 is about the set; everything below it is about the specimen.
        const centre = index === 0 ? SET_CENTRE : HERO_CENTRE;

        return boundsFrom(centre, realSpan, imaginarySpan);
    });
};
